import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { AdminThemeProvider, useAdminTheme } from './contexts/AdminThemeContext';

// Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';

// Auth Components
import StyledAuth from './components/auth/StyledAuth';

// Pages
import Dashboard from './components/pages/Dashboard';
import PaymentRequests from './components/pages/PaymentRequests';
import CreatePayment from './components/pages/CreatePayment';
import PaymentDetails from './components/pages/PaymentDetails';
import ServiceComparisonPage from './components/pages/ServiceComparisonPage';
import UserDashboard from './components/pages/UserDashboard';
import UserBookings from './components/pages/UserBookings';
import Success from './components/pages/Success';
import Cancel from './components/pages/Cancel';
import Unauthorized from './components/pages/Unauthorized';
import NotFound from './components/pages/NotFound';
import Greeters from './components/pages/Greeters';
import GreeterDashboard from './components/pages/GreeterDashboard';
import FinanceOperatorDashboard from './components/pages/FinanceOperatorDashboard';
import EnhancedEmailDashboard from './components/pages/EnhancedEmailDashboard';
import WhatsAppDashboard from './components/pages/WhatsAppDashboard';
import AdminProfile from './components/pages/AdminProfile';
import UserProfile from './components/pages/UserProfile';
import GreeterProfile from './components/pages/GreeterProfile';
import FinanceOperatorProfile from './components/pages/FinanceOperatorProfile';

// Styles
import './App.css';

function App() {
  return (
    <AuthProvider>
      <AdminThemeProvider>
        <AppContent />
      </AdminThemeProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { isDarkMode } = useAdminTheme();
  
  return (
    <div
      className={`min-h-screen flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-page-dark' : 'bg-skyone-page'
      }`}
    >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<StyledAuth />} />
          <Route path="/signup" element={<StyledAuth />} />
          <Route path="/styled-auth" element={<StyledAuth />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          
          {/* Success/Cancel pages (no auth required) */}
          <Route path="/success" element={<Success />} />
          <Route path="/cancel" element={<Cancel />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute requireAdmin={true}>
              <Header />
              <main className="flex-1">
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="payments" element={<PaymentRequests />} />
                  <Route path="create-payment" element={<CreatePayment />} />
                  <Route path="payment/:id" element={<PaymentDetails />} />
                  <Route path="services" element={<ServiceComparisonPage />} />
                  <Route path="greeters" element={<Greeters />} />
                  <Route path="enhanced-email" element={<EnhancedEmailDashboard />} />
                  <Route path="whatsapp" element={<WhatsAppDashboard />} />
                  <Route path="profile" element={<AdminProfile />} />
                </Routes>
              </main>
              <Footer />
            </ProtectedRoute>
          } />
          
          {/* Protected User Routes */}
          <Route path="/user/*" element={
            <ProtectedRoute requireUser={true}>
              <main className="flex-1">
                <Routes>
                  <Route path="dashboard" element={<UserDashboard />} />
                  <Route path="bookings" element={<UserBookings />} />
                  <Route path="profile" element={<UserProfile />} />
                </Routes>
              </main>
            </ProtectedRoute>
          } />
          
          {/* Protected Greeter Routes */}
          <Route path="/greeter/*" element={
            <ProtectedRoute requireGreeter={true}>
              <main className="flex-1">
                <Routes>
                  <Route path="dashboard" element={<GreeterDashboard />} />
                  <Route path="profile" element={<GreeterProfile />} />
                </Routes>
              </main>
            </ProtectedRoute>
          } />
          
          {/* Protected Finance Operator Routes */}
          <Route path="/finance/*" element={
            <ProtectedRoute requireFinanceOperator={true}>
              <Header />
              <main className="flex-1">
                <Routes>
                  <Route path="dashboard" element={<FinanceOperatorDashboard />} />
                  <Route path="profile" element={<FinanceOperatorProfile />} />
                </Routes>
              </main>
              <Footer />
            </ProtectedRoute>
          } />
          
          {/* Root redirect based on auth status */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Catch all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        
        <ToastContainer
          position="top-right"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
          className="toast-container"
        />
      </div>
  );
}

export default App;
